
import QRCode from 'qrcode';

/**
 * Generates a QR code data URL from a string
 * @param text Text to encode in the QR code
 * @returns Promise that resolves to QR code data URL
 */
export const generateQRCode = async (text: string): Promise<string> => {
  try {
    // Use high error correction level for better readability
    const options = {
      errorCorrectionLevel: 'H',
      margin: 1,
      width: 280,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    };
    
    // Format the text to ensure it has proper LHP- prefix without accents
    const formattedText = formatQRCode(text);
    
    // Generate QR code as data URL
    return await QRCode.toDataURL(formattedText, options);
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Format QR code to ensure it has the LHP- prefix
 * @param code Raw QR code string
 * @returns Formatted QR code with LHP- prefix
 */
export const formatQRCode = (code: string): string => {
  if (!code) return '';
  
  // Normalize by removing accents/diacritics
  const normalizedCode = removeAccents(code).toUpperCase();
  
  // If already has prefix, return as is
  if (normalizedCode.startsWith('LHP-')) {
    return normalizedCode;
  }
  
  // Otherwise, add the prefix
  return `LHP-${normalizedCode}`;
};

/**
 * Removes accents/diacritics from a string
 * @param str String to process
 * @returns String without accents/diacritics
 */
export const removeAccents = (str: string): string => {
  if (!str) return '';
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
